import CustomTooltip from '../../../shared/CustomTooltip';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    alpha,
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    Popover,
    Stack,
    Typography,
    useMediaQuery,
    Alert,
} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import React, { useMemo, useRef, useState } from 'react';
import { styled, useTheme } from '@mui/system';
import { OwnerRole } from '../../../enums/users';
import { TransitionGroup } from 'react-transition-group';
import { useGetPlatformSettingsQuery } from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import {
    capitalizeName,
    formatCurrency,
    groupCleaningFees,
    replaceSpacesWithHyphens,
} from '../../../../shared/utils';
import moment from 'moment';
import { TooltipCancellationContent } from '../../Policies/PolicyTooltipContent';
import { getCleaningFeeText } from '../../Experiences/Owner/Pricing/CleaningFeeCalendarView';
import { bookingPoliciesContent } from '../../Policies/PolicyForm';
import DiscountModal from './DiscountModal';
import { theme } from '../../../../shared/theme';

const LinkItem = styled('li')<{ isFixed: boolean }>(({ isFixed }) => ({
    cursor: isFixed ? 'default' : 'default',
    textDecoration: isFixed ? 'none' : 'none',
}));

const StyledTooltip = styled(({ className, ...props }: any) => (
    <CustomTooltip {...props} classes={{ popper: className }} />
))(() => ({
    [`& .MuiTooltip-tooltip`]: {
        backgroundColor: theme.palette.neutral.main,
        color: 'rgba(0,0,0,0.87)',
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
        padding: '10px 12px',
        maxWidth: 360,
    },
    [`& .MuiTooltip-arrow`]: {
        color: theme.palette.neutral.main,
    },
    [`& .MuiTooltip-arrow::before`]: {
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        boxSizing: 'border-box',
    },
}));

const InfoIconButton = ({
    tooltip,
    placement = 'left',
    onClick,
    clickable,
    iconSx,
    buttonSx,
    ariaLabel,
}: {
    tooltip: React.ReactNode;
    placement?: any;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    clickable?: boolean;
    iconSx?: any;
    buttonSx?: any;
    ariaLabel?: string;
}) => {
    const btn = (
        <IconButton
            size='small'
            onClick={onClick}
            aria-label={ariaLabel}
            sx={{
                p: 0.4,
                borderRadius: 999,
                // border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                backgroundColor: theme.palette.neutral.main,
                color: theme.palette.text.secondary,
                transition: 'all 140ms ease',
                '&:hover': {
                    backgroundColor: theme.palette.custom.cardActionBackground,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                },
                '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                },
                ...buttonSx,
            }}
        >
            <HelpOutlineIcon
                sx={{
                    fontSize: 16,
                    ...iconSx,
                }}
            />
        </IconButton>
    );

    if (clickable) return btn;

    return (
        <StyledTooltip
            title={<Box sx={{ '& p': { m: 0 } }}>{tooltip}</Box>}
            placement={placement}
            arrow
            enterTouchDelay={0}
        >
            <span>{btn}</span>
        </StyledTooltip>
    );
};

const ExpenseDetailsTooltip = ({
    date,
    description,
}: {
    date?: any;
    description?: string;
}) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
            variant='body2'
            sx={{ fontWeight: 800, letterSpacing: 0.2 }}
        >
            Expense details
        </Typography>
        <Typography
            variant='caption'
            sx={{ color: theme.palette.text.secondary }}
        >
            {date ? moment(date).format('D MMM YYYY') : ''}
        </Typography>
        <Typography variant='body2' sx={{ lineHeight: 1.35 }}>
            {description}
        </Typography>
    </Box>
);

const DiscountRow = ({ discount }: { discount: number }) => (
    <PriceRow
        left={
            <Stack direction='row' alignItems='center' spacing={0.75}>
                <Typography
                    variant='body2'
                    component='span'
                    sx={{ fontWeight: 500 }}
                >
                    Discount
                </Typography>
                <InfoIconButton
                    tooltip={discountScopeTooltip}
                    placement='top'
                    ariaLabel='Discount scope'
                />
            </Stack>
        }
        right={`- ${formatCurrency(discount)}`}
        rightSx={{ fontWeight: 600 }}
    />
);

const SectionCard = ({
    children,
    sx,
}: {
    children: React.ReactNode;
    sx?: any;
}) => (
    <Box
        sx={{
            // border: `1px solid ${theme.palette.custom.lightBorderColor}`,
            borderRadius: 3,
            // backgroundColor: theme.palette.custom.cardActionBackground,
            p: 2,
            ...sx,
        }}
    >
        {children}
    </Box>
);

const SectionHeader = ({
    title,
    tooltip,
    onInfoClick,
    showInfoButton,
}: {
    title: string;
    tooltip?: React.ReactNode;
    onInfoClick?: (e: React.MouseEvent<HTMLElement>) => void;
    showInfoButton?: boolean;
}) => (
    <Grid
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1,
        }}
    >
        <Typography
            variant='h3'
            sx={{ letterSpacing: 0.2, textTransform: 'uppercase' }}
        >
            {title}
        </Typography>

        {tooltip ? (
            showInfoButton ? (
                <InfoIconButton
                    tooltip={tooltip}
                    clickable
                    onClick={onInfoClick}
                    ariaLabel={`${title} info`}
                />
            ) : (
                <InfoIconButton
                    tooltip={tooltip}
                    placement='left'
                    ariaLabel={`${title} info`}
                />
            )
        ) : null}
    </Grid>
);

const TotalHeader = ({
    title,
    subTitle,
    amount,
    statusLabel,
    tooltip,
    onInfoClick,
    showInfoButton,
}: {
    title: string;
    subTitle?: React.ReactNode;
    amount: React.ReactNode;
    statusLabel?: string;
    tooltip?: React.ReactNode;
    onInfoClick?: (e: React.MouseEvent<HTMLElement>) => void;
    showInfoButton?: boolean;
}) => (
    <Grid
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1,
        }}
    >
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ minWidth: 0 }}
            >
                <Typography
                    variant='h3'
                    sx={{
                        letterSpacing: 0.2,
                        textTransform: 'uppercase',
                    }}
                >
                    {title}
                </Typography>

                {tooltip ? (
                    showInfoButton ? (
                        <InfoIconButton
                            tooltip={tooltip}
                            clickable
                            onClick={onInfoClick}
                            ariaLabel={`${title} info`}
                        />
                    ) : (
                        <InfoIconButton
                            tooltip={tooltip}
                            placement='left'
                            ariaLabel={`${title} info`}
                        />
                    )
                ) : null}
            </Stack>

            {subTitle ? (
                <Typography
                    variant='caption'
                    sx={{
                        display: 'block',
                        color: theme.palette.text.secondary,
                        mt: 0.25,
                        lineHeight: 1.25,
                    }}
                >
                    {subTitle}
                </Typography>
            ) : null}
        </Box>

        <Stack
            direction='column'
            alignItems='flex-end'
            spacing={0.5}
            sx={{ flexShrink: 0 }}
        >
            <Typography
                variant='body2'
                sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}
            >
                {amount}
            </Typography>

            {statusLabel ? (
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 0.9,
                        py: 0.2,
                        borderRadius: 999,
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: 0.2,
                        whiteSpace: 'nowrap',
                        border: `1px solid ${theme.palette.error.main}`,
                        color: theme.palette.error.main,
                        backgroundColor: 'rgba(208, 52, 44, 0.06)',
                        lineHeight: 1.3,
                    }}
                >
                    {statusLabel}
                </Box>
            ) : null}
        </Stack>
    </Grid>
);

const PriceRow = ({
    left,
    right,
    subLeft,
    rightSx,
    rowSx,
}: {
    left: React.ReactNode;
    right: React.ReactNode;
    subLeft?: React.ReactNode;
    rightSx?: any;
    rowSx?: any;
}) => {
    const label =
        typeof left === 'string' ? replaceSpacesWithHyphens(left) : undefined;

    return (
        <Grid
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: subLeft ? 'flex-start' : 'center',
                width: '100%',
                gap: 1,
                ...rowSx,
            }}
        >
            <Box sx={{ flex: 1, pr: 1, minWidth: 0 }}>
                <Box>
                    {typeof left === 'string' ? (
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 500 }}
                            id={label}
                        >
                            {left}
                        </Typography>
                    ) : (
                        left
                    )}
                </Box>

                {subLeft ? (
                    <Typography
                        variant='caption'
                        sx={{
                            display: 'block',
                            color: theme.palette.text.secondary,
                            mt: 0.25,
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                        }}
                    >
                        {subLeft}
                    </Typography>
                ) : null}
            </Box>

            <Typography
                variant='body2'
                sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    ...rightSx,
                }}
                aria-labelledby={label}
            >
                {right}
            </Typography>
        </Grid>
    );
};

const popoverPaperSx = {
    borderRadius: 12,
    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
    boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
};

/** Merged into `expenses` on edit reservation for catering/massage line totals (see reservationEdit.utils). */
const OPTIONAL_SERVICE_ESTIMATE_SOURCE = 'optionalServiceEstimate';

const discountScopeTooltip = (
    <Typography variant='body2'>
        This discount applies to reservation booking fees only. It does not
        apply to additional expenses or add-ons.
    </Typography>
);

const BookingCharges = ({
    charges,
    bookingTotal,
    module,
    invoice,
    expenses,
    refunds,
    reservation,
    billingExcluded,
    manualDiscount = false,
    isEditing,
}: {
    charges: any;
    module?: any;
    bookingTotal: any;
    invoice?: any;
    expenses?: any;
    refunds?: any;
    reservation?: any;
    billingExcluded?: boolean;
    manualDiscount?: boolean;
    isEditing?: boolean;
}) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    useGetPlatformSettingsQuery('/platform-settings');
    const loggedInUser = UserRole();
    const firstTextFieldRef = useRef(null);
    const theme = useTheme();

    const [showCleaningFeeDetails, setShowCleaningFeeDetails] = useState(false);
    const [showDiscountModal, setShowDiscountModal] = useState(false);

    const [feeInfoAnchor, setFeeInfoAnchor] = useState<HTMLElement | null>(
        null,
    );
    const [expenseInfoAnchor, setExpenseInfoAnchor] =
        useState<HTMLElement | null>(null);
    const [rowInfoAnchor, setRowInfoAnchor] = useState<HTMLElement | null>(
        null,
    );
    const [rowInfoContent, setRowInfoContent] = useState<{
        date?: any;
        description?: string;
    } | null>(null);

    const chargesWithIds = charges.map((charge: any, index: number) => ({
        ...charge,
        feeId: `charge-${index}`,
    }));

    const modifiedCharges = useMemo(
        () => groupCleaningFees(chargesWithIds),
        [chargesWithIds],
    );

    const totalExpenses =
        expenses?.reduce(
            (total: any, expense: { amount: any }) =>
                total + (expense.amount || 0),
            0,
        ) ?? 0;

    const hasExpenses = (expenses?.length ?? 0) > 0;

    const { additionalSubtotalLabel, hasOptionalEstimates } = useMemo(() => {
        let optionalTotal = 0;
        let billingCount = 0;
        for (const expense of expenses ?? []) {
            if ((expense as any)?.source === OPTIONAL_SERVICE_ESTIMATE_SOURCE) {
                optionalTotal += Number((expense as any).amount) || 0;
            } else {
                billingCount += 1;
            }
        }
        const hasOpt = optionalTotal > 0;
        let label: string;
        if (!isEditing) {
            label = 'Total Additional Expenses (inc GST)';
        } else if (hasOpt && billingCount === 0) {
            label = 'Total Additional Services (ex GST)';
        } else if (hasOpt && billingCount > 0) {
            label = 'Total Additional Services & Expenses';
        } else {
            label = 'Total Additional Expenses (inc GST)';
        }
        return {
            additionalSubtotalLabel: label,
            hasOptionalEstimates: hasOpt,
        };
    }, [expenses, isEditing]);

    const effectiveBookingTotal = billingExcluded
        ? 0
        : (bookingTotal?.total ?? 0);
    const grandTotal = (effectiveBookingTotal ?? 0) + (totalExpenses ?? 0);

    const getFeeMultiplierText = (feeMultiplier: any) => {
        const { value, type } = feeMultiplier;

        if (value > 1) {
            switch (type) {
                case 'night':
                    return 'nights';
                case 'day':
                    return 'days';
                case 'guest':
                    return 'guests';
                case 'hour':
                    return 'hours';
                default:
                    return type;
            }
        }
        return type;
    };

    const openRowInfo = (
        e: React.MouseEvent<HTMLElement>,
        payload?: { date?: any; description?: string },
    ) => {
        if (!payload?.description) return;
        setRowInfoAnchor(e.currentTarget);
        setRowInfoContent(payload);
    };

    const closeRowInfo = () => {
        setRowInfoAnchor(null);
        setRowInfoContent(null);
    };

    const openFeeInfo = (e: React.MouseEvent<HTMLElement>) => {
        setFeeInfoAnchor(e.currentTarget);
    };

    const closeFeeInfo = () => {
        setFeeInfoAnchor(null);
    };

    const openExpenseInfo = (e: React.MouseEvent<HTMLElement>) => {
        setExpenseInfoAnchor(e.currentTarget);
    };

    const closeExpenseInfo = () => {
        setExpenseInfoAnchor(null);
    };

    const feeInfoOpen = Boolean(feeInfoAnchor);
    const expenseInfoOpen = Boolean(expenseInfoAnchor);
    const rowInfoOpen =
        Boolean(rowInfoAnchor) && Boolean(rowInfoContent?.description);

    const canShowDiscountAction =
        !isEditing &&
        loggedInUser?.role === OwnerRole.Admin &&
        !reservation?.bill &&
        !reservation?.invoice;

    const discountLabel =
        (reservation?.bookingTotal?.discount ?? 0) > 0
            ? 'Update discount'
            : 'Apply discount';

    const invoiceStatus =
        invoice?.status ??
        reservation?.invoice?.status ??
        reservation?.bill?.status;

    const totalStatusLabel =
        invoiceStatus === 'pending' || invoiceStatus === 'pendingPayment'
            ? 'Pending payment'
            : invoiceStatus === 'paid'
              ? 'Paid'
              : undefined;

    return (
        <Grid sx={{ width: '100%' }}>
            <SectionCard>
                <SectionHeader
                    title='Booking Expenses'
                    tooltip={
                        <Typography variant='body2'>
                            Fees at the time the booking was reserved.
                            Additional expenses may be incurred.
                        </Typography>
                    }
                    showInfoButton={isMobile}
                    onInfoClick={openFeeInfo}
                />

                {canShowDiscountAction ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            mb: 1.5,
                            borderRadius: 2,
                            backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.08,
                            ),
                        }}
                    >
                        <Stack
                            direction='row'
                            alignItems='center'
                            spacing={0.75}
                        >
                            <Typography
                                variant='body2'
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    letterSpacing: 0.2,
                                }}
                            >
                                Discount
                            </Typography>
                            <InfoIconButton
                                tooltip={discountScopeTooltip}
                                placement='top'
                                ariaLabel='Discount scope'
                            />
                        </Stack>
                        <Button
                            onClick={() => setShowDiscountModal(true)}
                            variant='outlined'
                            size='small'
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 1.5,
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                px: 1.5,
                                py: 0.5,
                                '&:hover': {
                                    borderColor: theme.palette.primary.dark,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.06,
                                    ),
                                },
                            }}
                        >
                            {discountLabel}
                        </Button>
                    </Box>
                ) : null}

                <Typography
                    component='ul'
                    variant='body2'
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        listStyle: 'none',
                        paddingLeft: 0,
                        margin: 0,
                    }}
                >
                    <TransitionGroup>
                        {(modifiedCharges ?? []).map(
                            (charge: any, index: number) => {
                                const isCleaningFee =
                                    charge.chargeType === 'cleaningFee';

                                const breakdownText = !isCleaningFee
                                    ? [
                                          `$${Number(
                                              charge?.unitPrice ?? 0,
                                          ).toLocaleString('en-AU', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                          })} AUD`,
                                          ...(charge?.feeMultipliers ?? []).map(
                                              (feeMultiplier: any) =>
                                                  `× ${feeMultiplier.value} ${getFeeMultiplierText(feeMultiplier)}`,
                                          ),
                                          charge?.isWeekendPrice
                                              ? '(Weekend Price)'
                                              : null,
                                      ]
                                          .filter(Boolean)
                                          .join(' ')
                                    : null;

                                return (
                                    <Collapse
                                        key={
                                            charge.feeId ??
                                            `${charge.chargeType}-${index}`
                                        }
                                    >
                                        <Grid
                                            container
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                pb: 0.25,
                                            }}
                                        >
                                            <LinkItem
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'flex-start',
                                                    width: '100%',
                                                    gap: 1,
                                                }}
                                                ref={
                                                    index === 0
                                                        ? firstTextFieldRef
                                                        : null
                                                }
                                                isFixed={
                                                    charge.type === 'fixed'
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        pr: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Stack
                                                        direction='row'
                                                        alignItems='center'
                                                        spacing={0.75}
                                                        sx={{
                                                            minWidth: 0,
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontWeight: 500,
                                                                lineHeight: 1.3,
                                                                wordBreak:
                                                                    'break-word',
                                                            }}
                                                        >
                                                            {isCleaningFee
                                                                ? capitalizeName(
                                                                      charge.name,
                                                                  )
                                                                : charge?.name
                                                                  ? capitalizeName(
                                                                        charge.name,
                                                                    )
                                                                  : capitalizeName(
                                                                        charge.chargeType,
                                                                    )}
                                                        </Typography>

                                                        {charge?.description ? (
                                                            <InfoIconButton
                                                                tooltip={
                                                                    <Typography variant='body2'>
                                                                        {
                                                                            charge.description
                                                                        }
                                                                    </Typography>
                                                                }
                                                                placement='top'
                                                                ariaLabel='Charge details'
                                                            />
                                                        ) : null}
                                                    </Stack>

                                                    {!isCleaningFee ? (
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                display:
                                                                    'block',
                                                                mt: 0.25,
                                                                lineHeight: 1.3,
                                                                wordBreak:
                                                                    'break-word',
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .secondary,
                                                            }}
                                                        >
                                                            {breakdownText}
                                                        </Typography>
                                                    ) : null}

                                                    {isCleaningFee &&
                                                    charge.cleaningFeeType !==
                                                        'flat' ? (
                                                        <Button
                                                            size='small'
                                                            onClick={() =>
                                                                setShowCleaningFeeDetails(
                                                                    (prev) =>
                                                                        !prev,
                                                                )
                                                            }
                                                            sx={{
                                                                textDecoration:
                                                                    'underline',
                                                                mt: 0.25,
                                                                p: 0,
                                                                minWidth:
                                                                    'auto',
                                                                fontSize: 11,
                                                                justifyContent:
                                                                    'flex-start',
                                                            }}
                                                        >
                                                            {showCleaningFeeDetails
                                                                ? 'Hide breakdown'
                                                                : 'Show breakdown'}
                                                        </Button>
                                                    ) : null}
                                                </Box>

                                                <Typography
                                                    variant='body2'
                                                    sx={{
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {formatCurrency(
                                                        charge?.total,
                                                    )}
                                                </Typography>
                                            </LinkItem>
                                        </Grid>

                                        {isCleaningFee ? (
                                            <Collapse
                                                in={showCleaningFeeDetails}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        pl: 2,
                                                        mt: 0.75,
                                                    }}
                                                >
                                                    <Stack sx={{ gap: 0.75 }}>
                                                        <TransitionGroup>
                                                            {charge?.details
                                                                ?.filter(
                                                                    (c: any) =>
                                                                        c.total >
                                                                        0,
                                                                )
                                                                .map(
                                                                    (
                                                                        detailCharge: any,
                                                                        idx: number,
                                                                    ) => {
                                                                        const detail =
                                                                            getCleaningFeeText(
                                                                                detailCharge,
                                                                                module?.spaces,
                                                                            );

                                                                        return (
                                                                            <Collapse
                                                                                key={
                                                                                    idx
                                                                                }
                                                                            >
                                                                                <PriceRow
                                                                                    left={
                                                                                        detail.description
                                                                                    }
                                                                                    right={
                                                                                        detail.amount
                                                                                    }
                                                                                    rightSx={{
                                                                                        fontWeight: 500,
                                                                                    }}
                                                                                />
                                                                            </Collapse>
                                                                        );
                                                                    },
                                                                )}
                                                        </TransitionGroup>
                                                    </Stack>
                                                </Box>
                                            </Collapse>
                                        ) : null}
                                    </Collapse>
                                );
                            },
                        )}
                    </TransitionGroup>
                </Typography>

                <Divider
                    sx={{
                        width: '100%',
                        my: 1.5,
                        borderColor: theme.palette.custom.lightBorderColor,
                    }}
                />

                <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}
                >
                    <Grid
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            width: '100%',
                            gap: 1,
                        }}
                        ref={firstTextFieldRef}
                    >
                        <Box sx={{ flex: 1, pr: 1, minWidth: 0 }}>
                            <Typography
                                variant='body2'
                                sx={{ fontWeight: 500 }}
                                id='subtotal'
                            >
                                Subtotal
                            </Typography>

                            {(bookingTotal?.discount ?? 0) > 0 ? (
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontStyle: 'italic',
                                        display: 'block',
                                        color: theme.palette.text.secondary,
                                        mt: 0.25,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    Discount applied to booking subtotal
                                </Typography>
                            ) : null}
                        </Box>

                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            aria-labelledby='subtotal'
                        >
                            {formatCurrency(bookingTotal.subTotal)}
                        </Typography>
                    </Grid>

                    {bookingTotal?.discount > 0 && !manualDiscount ? (
                        <DiscountRow discount={bookingTotal?.discount} />
                    ) : null}

                    {!!bookingTotal?.guestServiceFee ? (
                        <PriceRow
                            left={`7DAY Service Fee ${bookingTotal.guestServiceFeePercentage}%`}
                            right={formatCurrency(bookingTotal.guestServiceFee)}
                            rightSx={{ fontWeight: 600 }}
                        />
                    ) : null}

                    {bookingTotal?.discount > 0 && manualDiscount ? (
                        <DiscountRow discount={bookingTotal?.discount} />
                    ) : null}

                    <PriceRow
                        left='GST (10%)'
                        right={formatCurrency(bookingTotal.gst)}
                        rightSx={{ fontWeight: 600 }}
                    />

                    <Divider
                        sx={{
                            width: '100%',
                            mt: 1,
                            borderColor: theme.palette.custom.lightBorderColor,
                        }}
                    />

                    <PriceRow
                        left='Booking Total (inc GST) *'
                        right={formatCurrency(effectiveBookingTotal)}
                        rightSx={{
                            fontWeight: 800,
                            textDecoration: billingExcluded
                                ? 'line-through'
                                : 'none',
                        }}
                        rowSx={{ mt: 0.5 }}
                    />

                    {reservation?.status === 'cancelled' &&
                    reservation?.approvedAt !== null ? (
                        <Box
                            sx={{
                                mt: 1,
                                px: 1.5,
                                py: 1.1,
                                borderRadius: 2,
                                backgroundColor: 'rgba(92, 181, 179, 0.7)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography
                                variant='body2'
                                sx={{
                                    fontWeight: 800,
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {capitalizeName(
                                    !reservation?.policy
                                        ? 'Fair'
                                        : reservation.policy
                                                ?.cancellationType ===
                                            'nonRefundable'
                                          ? 'Non-Refundable'
                                          : reservation.policy
                                                ?.cancellationType,
                                )}{' '}
                                Cancellation Policy – {refunds?.percentage ?? 0}
                                % refund
                                <StyledTooltip
                                    title={
                                        <TooltipCancellationContent
                                            content={bookingPoliciesContent}
                                        />
                                    }
                                    placement='top'
                                    arrow
                                    enterTouchDelay={0}
                                >
                                    <span>
                                        <IconButton
                                            size='small'
                                            sx={{
                                                p: 0.15,
                                                ml: 0.25,
                                                color: 'white',
                                                borderRadius: 999,
                                                '&:hover': {
                                                    backgroundColor:
                                                        'rgba(255,255,255,0.14)',
                                                },
                                                '&:focus-visible': {
                                                    outline:
                                                        '2px solid rgba(255,255,255,0.9)',
                                                    outlineOffset: 2,
                                                },
                                            }}
                                        >
                                            <HelpOutlineIcon
                                                sx={{ fontSize: 16 }}
                                            />
                                        </IconButton>
                                    </span>
                                </StyledTooltip>
                            </Typography>

                            <Typography
                                variant='body2'
                                sx={{
                                    fontWeight: 800,
                                    color: 'white',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatCurrency(
                                    refunds?.total ? refunds?.total : 0,
                                )}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
            </SectionCard>

            <Box sx={{ mt: 2 }}>
                <SectionCard>
                    <SectionHeader
                        title={
                            isEditing
                                ? 'Additional Services'
                                : 'Additional Expenses'
                        }
                        tooltip={
                            <Typography variant='body2'>
                                {isEditing ? (
                                    <>
                                        Optional catering and wellness &
                                        lifestyle service estimates reflect line
                                        items you have entered (ex GST). Posted
                                        incidentals and other charges may also
                                        appear here after your stay.
                                    </>
                                ) : (
                                    <>
                                        There may be additional expenses billed
                                        after your stay, such as incidentals,
                                        damage, excessive cleaning, late
                                        checkout, or extra services.
                                    </>
                                )}
                            </Typography>
                        }
                        showInfoButton={isMobile}
                        onInfoClick={openExpenseInfo}
                    />

                    <Divider
                        sx={{
                            width: '100%',
                            mb: 1.25,
                            borderColor: theme.palette.custom.lightBorderColor,
                        }}
                    />

                    {!hasExpenses ? (
                        <Typography
                            variant='body2'
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            {isEditing
                                ? 'No additional services or billing charges yet.'
                                : 'No additional expenses have been applied.'}
                        </Typography>
                    ) : (
                        <Stack sx={{ gap: 1 }}>
                            <TransitionGroup>
                                {expenses?.map(
                                    (expense: any, index: number) => {
                                        const hasDetails = Boolean(
                                            expense?.description,
                                        );

                                        return (
                                            <Collapse
                                                key={
                                                    expense.id ??
                                                    `${expense.source ?? 'expense'}-${expense.name}-${index}`
                                                }
                                            >
                                                <Grid
                                                    container
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            alignItems:
                                                                'flex-start',
                                                            gap: 1,
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                flex: 1,
                                                                pr: 1,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Stack
                                                                direction='row'
                                                                alignItems='center'
                                                                spacing={0.75}
                                                                sx={{
                                                                    minWidth: 0,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant='body2'
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        wordBreak:
                                                                            'break-word',
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    {capitalizeName(
                                                                        expense.name,
                                                                    )}
                                                                </Typography>

                                                                {hasDetails ? (
                                                                    isMobile ? (
                                                                        <InfoIconButton
                                                                            tooltip={
                                                                                <span />
                                                                            }
                                                                            clickable
                                                                            ariaLabel='Expense details'
                                                                            onClick={(
                                                                                e,
                                                                            ) =>
                                                                                openRowInfo(
                                                                                    e,
                                                                                    {
                                                                                        date: expense.date,
                                                                                        description:
                                                                                            expense.description,
                                                                                    },
                                                                                )
                                                                            }
                                                                            buttonSx={{
                                                                                p: 0.3,
                                                                                backgroundColor:
                                                                                    theme
                                                                                        .palette
                                                                                        .neutral
                                                                                        .main,
                                                                            }}
                                                                            iconSx={{
                                                                                fontSize: 15,
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <InfoIconButton
                                                                            tooltip={
                                                                                <ExpenseDetailsTooltip
                                                                                    date={
                                                                                        expense.date
                                                                                    }
                                                                                    description={
                                                                                        expense.description
                                                                                    }
                                                                                />
                                                                            }
                                                                            placement='top'
                                                                            ariaLabel='Expense details'
                                                                            buttonSx={{
                                                                                p: 0.3,
                                                                                backgroundColor:
                                                                                    theme
                                                                                        .palette
                                                                                        .neutral
                                                                                        .main,
                                                                            }}
                                                                            iconSx={{
                                                                                fontSize: 15,
                                                                            }}
                                                                        />
                                                                    )
                                                                ) : null}
                                                            </Stack>

                                                            {expense
                                                                ?.attachments
                                                                ?.length ? (
                                                                <Box
                                                                    sx={{
                                                                        mt: 0.4,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant='caption'
                                                                        sx={{
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .secondary,
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 0.5,
                                                                            flexWrap:
                                                                                'wrap',
                                                                        }}
                                                                    >
                                                                        <AttachFileIcon
                                                                            sx={{
                                                                                fontSize: 12,
                                                                            }}
                                                                        />
                                                                        {expense.attachments
                                                                            ?.map(
                                                                                (
                                                                                    attachment: any,
                                                                                    idx: number,
                                                                                ) => (
                                                                                    <Typography
                                                                                        variant='body2'
                                                                                        key={
                                                                                            idx
                                                                                        }
                                                                                        onClick={() =>
                                                                                            attachment.src &&
                                                                                            window.open(
                                                                                                attachment.src,
                                                                                                '_blank',
                                                                                            )
                                                                                        }
                                                                                        sx={{
                                                                                            cursor: 'pointer',
                                                                                            textDecoration:
                                                                                                'underline',
                                                                                            display:
                                                                                                'inline',
                                                                                            color: theme
                                                                                                .palette
                                                                                                .text
                                                                                                .secondary,
                                                                                        }}
                                                                                    >
                                                                                        Attachment{' '}
                                                                                        {`#${idx + 1}`}
                                                                                    </Typography>
                                                                                ),
                                                                            )
                                                                            .reduce(
                                                                                (
                                                                                    prev: any,
                                                                                    curr: any,
                                                                                ): any => [
                                                                                    prev,
                                                                                    ', ',
                                                                                    curr,
                                                                                ],
                                                                            )}
                                                                    </Typography>
                                                                </Box>
                                                            ) : null}
                                                        </Box>

                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontWeight: 600,
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {formatCurrency(
                                                                expense?.amount,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Collapse>
                                        );
                                    },
                                )}
                            </TransitionGroup>

                            <Divider
                                sx={{
                                    width: '100%',
                                    mt: 1,
                                    borderColor:
                                        theme.palette.custom.lightBorderColor,
                                }}
                            />

                            <PriceRow
                                left={additionalSubtotalLabel}
                                right={formatCurrency(totalExpenses)}
                                rightSx={{ fontWeight: 800 }}
                                rowSx={{ mt: 0.5 }}
                            />
                        </Stack>
                    )}
                </SectionCard>
            </Box>

            <Box sx={{ mt: 2 }}>
                <SectionCard>
                    <TotalHeader
                        title='Total Charges'
                        amount={formatCurrency(grandTotal)}
                        statusLabel={totalStatusLabel}
                        tooltip={
                            <Typography variant='body2'>
                                {isEditing
                                    ? 'Your overall total combines the booking total with optional service estimates and any posted additional charges.'
                                    : 'Your overall total combines the booking total with any additional expenses.'}
                            </Typography>
                        }
                    />

                    <Divider
                        sx={{
                            width: '100%',
                            mb: 1.25,
                            borderColor: theme.palette.custom.lightBorderColor,
                        }}
                    />

                    <Stack sx={{ gap: 0.9 }}>
                        <PriceRow
                            left='Booking Total (inc GST)'
                            right={formatCurrency(effectiveBookingTotal)}
                            rightSx={{
                                fontWeight: 800,
                                textDecoration: billingExcluded
                                    ? 'line-through'
                                    : 'none',
                            }}
                        />

                        <PriceRow
                            left={additionalSubtotalLabel}
                            right={formatCurrency(totalExpenses)}
                            rightSx={{ fontWeight: 700 }}
                        />

                        <Divider
                            sx={{
                                width: '100%',
                                mt: 0.5,
                                borderColor:
                                    theme.palette.custom.lightBorderColor,
                            }}
                        />

                        <PriceRow
                            left='Total (Booking + Additional)'
                            right={formatCurrency(grandTotal)}
                            rightSx={{
                                fontWeight: 800,
                            }}
                            rowSx={{ mt: 0.5 }}
                        />
                    </Stack>
                </SectionCard>
            </Box>

            {!invoice && loggedInUser?.role.includes('guest') ? (
                <Alert
                    severity='info'
                    sx={{
                        mt: 2,
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        boxShadow: 'none',
                        borderRadius: 2,
                        fontSize: 12,
                        whiteSpace: 'normal' as const,
                        wordBreak: 'break-word' as const,
                        overflowWrap: 'break-word' as const,
                        backgroundColor: 'rgba(92, 181, 179, 0.06)',
                    }}
                >
                    {billingExcluded ? (
                        <Typography
                            variant='body2'
                            sx={{
                                display: 'block',
                                color: 'text.primary',
                                mt: 0.25,
                            }}
                        >
                            *The host has opted to exclude you from billing for
                            this experience, so no charges will apply for this
                            reservation.
                        </Typography>
                    ) : (
                        <Typography
                            variant='body2'
                            sx={{
                                display: 'block',
                                color: 'text.primary',
                                mt: 0.25,
                            }}
                        >
                            * You won&apos;t be charged yet – billing will be
                            included in your monthly invoice once the experience
                            has concluded for this period.
                        </Typography>
                    )}

                    {expenses && !isEditing ? (
                        <Typography
                            variant='body2'
                            sx={{
                                display: 'block',
                                color: 'text.primary',
                                mt: 0.75,
                            }}
                        >
                            Note: Pricing and fees are based on the time of
                            booking and do not include optional services such as
                            catering, which will be invoiced separately. Final
                            charges may vary depending on the services provided
                            on the day.
                        </Typography>
                    ) : null}
                    {expenses && isEditing && hasOptionalEstimates ? (
                        <Typography
                            variant='body2'
                            sx={{
                                display: 'block',
                                color: 'text.primary',
                                mt: 0.75,
                            }}
                        >
                            Note: Catering and Wellness & lifestyle service
                            amounts are estimates from your current requests (ex
                            GST). Final charges may differ once services are
                            confirmed.
                        </Typography>
                    ) : null}
                </Alert>
            ) : null}

            <Popover
                open={feeInfoOpen}
                anchorEl={feeInfoAnchor}
                onClose={closeFeeInfo}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: popoverPaperSx }}
            >
                <Box sx={{ p: 1.25, maxWidth: 340 }}>
                    <Typography variant='body2'>
                        Fees at the time the booking was reserved. Additional
                        expenses may be incurred.
                    </Typography>
                </Box>
            </Popover>

            <Popover
                open={expenseInfoOpen}
                anchorEl={expenseInfoAnchor}
                onClose={closeExpenseInfo}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: popoverPaperSx }}
            >
                <Box sx={{ p: 1.25, maxWidth: 340 }}>
                    <Typography variant='body2'>
                        {isEditing ? (
                            <>
                                Optional service lines show estimates from your
                                current catering, alcohol, wellness & lifestyle
                                service requests (ex GST). Other lines are
                                posted charges. Additional incidentals may still
                                be billed after your stay.
                            </>
                        ) : (
                            <>
                                While your reservation covers standard costs at
                                the time of booking, there may be additional
                                expenses billed after your stay, such as
                                incidentals, damage, excessive cleaning, late
                                checkout, or extra services.
                            </>
                        )}
                    </Typography>
                </Box>
            </Popover>

            <Popover
                open={rowInfoOpen}
                anchorEl={rowInfoAnchor}
                onClose={closeRowInfo}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: popoverPaperSx }}
            >
                <Box sx={{ p: 1.25, maxWidth: 360 }}>
                    <ExpenseDetailsTooltip
                        date={rowInfoContent?.date}
                        description={rowInfoContent?.description}
                    />
                </Box>
            </Popover>

            <DiscountModal
                open={showDiscountModal}
                onClose={() => setShowDiscountModal(false)}
                reservation={reservation}
            />
        </Grid>
    );
};

export default BookingCharges;
